import { Menu, Spin } from 'antd';

import CourierSubFilterMenuBody from '../CourierSubFilterMenuBody';
import AlertAssignmentSubFilterMenuBody from '../AlertAssignmentSubFilterMenuBody';
import LocationsGroupedSubFilterMenuBody from '../LocationsGroupedSubFilterMenuBody';
import type {
  AppliedFilter,
  ListItem,
  LocationRow,
  SecondaryFilterOption,
} from '../../ControlTower/types';
import type { AlertSection } from '../CustomAlertThresholdFilter';
import AlertsBody from './AlertsBody';
import GroupedLocationsPaginatedList from './GroupedLocationsPaginatedList';
import { PaginatedOptionsList } from './PaginatedOptionsList';
import SubFilterOptionsList from './SubFilterOptionsList';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import { extractGroupedLocationLeafOptions } from '../utils/groupedLocationLeafIds';
import { SUBFILTER_LOADING_MIN_HEIGHT } from '../utils/labels';

type PaginationState = {
  data: ListItem[];
  loading: boolean;
  loadMore: () => void;
  fetchAllPages?: () => Promise<ListItem[]>;
};

type SubFilterBodyProps = {
  filterBy: string;
  loading: boolean;
  isPaginated: boolean;
  isGroupedLocations: boolean;
  listToRender: SecondaryFilterOption[];
  secondaryFiltersSource: SecondaryFilterOption[];
  selectedOptions: SecondaryFilterOption[];
  appliedFilters: AppliedFilter[];
  searchQuery: string;
  hiddenOptionIds?: Set<string>;
  paginatedListHeight: number;
  pg: PaginationState;
  stopAll: (event: React.MouseEvent) => void;
  setSelectedOptions?: (options: ListItem[]) => void;
  handleAlertThresholdSelect?: (payload: {
    type: string;
    item?: { id: string; name: string };
  }) => void;
  handleCheckboxChange: (
    event: CheckboxChangeEvent,
    option: SecondaryFilterOption
  ) => void;
  handleApplyPresetFilters?: (
    presetIds: { id: string | number }[],
    appendMode: boolean
  ) => void;
  onDeletePreset?: (presetId: string) => void;
  alertFilterConfigOverride?: AlertSection[];
};

const SubFilterBody: React.FC<SubFilterBodyProps> = ({
  filterBy,
  loading,
  isPaginated,
  isGroupedLocations,
  listToRender,
  secondaryFiltersSource,
  selectedOptions,
  appliedFilters,
  searchQuery,
  hiddenOptionIds,
  paginatedListHeight,
  pg,
  stopAll,
  setSelectedOptions,
  handleAlertThresholdSelect,
  handleCheckboxChange,
  handleApplyPresetFilters,
  onDeletePreset,
  alertFilterConfigOverride,
}) => {
  const namedSelection = selectedOptions.map((opt) => ({
    ...opt,
    name: opt.name ?? String(opt.id),
  }));
  // The grouped state list wants a React state setter; the rest of this file
  // passes a plain "replace the selection" function.
  const stateSetter: React.ComponentProps<
    typeof LocationsGroupedSubFilterMenuBody
  >['setSelectedOptions'] = setSelectedOptions
    ? (next) =>
        setSelectedOptions(
          // The list may hold numeric ids; the selection keeps string ids.
          (typeof next === 'function' ? next(namedSelection) : next).map(
            (opt) => ({ ...opt, id: String(opt.id) })
          )
        )
    : undefined;

  if (loading) {
    return (
      <div
        className='subfilter-options-loading'
        // Shares the number with the height estimate in useSubFilterMenuState.
        // eslint-disable-next-line react/forbid-dom-props
        style={{ minHeight: SUBFILTER_LOADING_MIN_HEIGHT }}
      >
        <Spin size='small' />
      </div>
    );
  }

  if (filterBy === 'alert_assignment') {
    return (
      <AlertAssignmentSubFilterMenuBody
        source={secondaryFiltersSource}
        searchQuery={searchQuery}
        selectedOptions={selectedOptions}
        setSelectedOptions={setSelectedOptions}
        hiddenOptionIds={hiddenOptionIds}
      />
    );
  }

  if (filterBy === 'alert_types') {
    return (
      <AlertsBody
        selectedOptions={selectedOptions}
        appliedFilters={appliedFilters}
        handleAlertThresholdSelect={handleAlertThresholdSelect}
        searchQuery={searchQuery}
        hiddenOptionIds={hiddenOptionIds}
        alertFilterConfigOverride={alertFilterConfigOverride}
      />
    );
  }

  if (filterBy === 'courier') {
    const UNBOOKED_OPTION: SecondaryFilterOption = {
      id: '__NO_COURIER__',
      name: 'Unbooked',
    };
    const courierSource = Array.isArray(secondaryFiltersSource)
      ? [...secondaryFiltersSource, UNBOOKED_OPTION]
      : [UNBOOKED_OPTION];

    return (
      <CourierSubFilterMenuBody
        hiddenOptionIds={hiddenOptionIds}
        source={courierSource}
        searchQuery={searchQuery}
        selectedOptions={selectedOptions.map((opt) => ({
          ...opt,
          id: String(opt.id),
          name: opt.name ?? String(opt.id),
        }))}
        setSelectedOptions={
          setSelectedOptions
            ? (opts) =>
                setSelectedOptions(
                  opts.map((opt) => ({
                    ...opt,
                    id: String(opt.id),
                  }))
                )
            : undefined
        }
      />
    );
  }

  if (filterBy === 'origin_state' || filterBy === 'destination_state') {
    return (
      <LocationsGroupedSubFilterMenuBody
        hiddenOptionIds={hiddenOptionIds}
        source={Array.isArray(pg.data) ? pg.data : []}
        searchQuery={searchQuery}
        selectedOptions={namedSelection}
        setSelectedOptions={stateSetter}
        fetchAllMatching={
          searchQuery.trim() && pg.fetchAllPages ? pg.fetchAllPages : undefined
        }
      />
    );
  }

  if (isPaginated && isGroupedLocations) {
    return (
      <div className='group-location'>
        <GroupedLocationsPaginatedList
          hiddenOptionIds={hiddenOptionIds}
          rows={(pg.data ?? ([] as unknown)) as LocationRow[]}
          loading={pg.loading}
          height={paginatedListHeight}
          onBottomReached={pg.loadMore}
          stopAll={stopAll}
          selectedOptions={selectedOptions}
          setSelectedOptions={
            setSelectedOptions
              ? // The list reports its selection as a list of { id, name }.
                (next: unknown) => setSelectedOptions(next as ListItem[])
              : undefined
          }
          onToggleSelectAll={
            setSelectedOptions
              ? async (checked) => {
                  if (!checked) {
                    // Prefer the full search match set when a search is active,
                    // so unloaded pages added by Select all are also cleared.
                    let rows = (pg.data ?? []) as LocationRow[];
                    if (searchQuery.trim() && pg.fetchAllPages) {
                      rows = (await pg.fetchAllPages()) as LocationRow[];
                    }
                    const removeIds = new Set(
                      extractGroupedLocationLeafOptions(rows)
                        .filter(
                          (option) => !hiddenOptionIds?.has(String(option.id))
                        )
                        .map((option) => option.id)
                    );
                    setSelectedOptions(
                      selectedOptions.filter(
                        (option) => !removeIds.has(String(option.id))
                      ) as ListItem[]
                    );
                    return;
                  }

                  let rows = (pg.data ?? []) as LocationRow[];
                  if (searchQuery.trim() && pg.fetchAllPages) {
                    rows = (await pg.fetchAllPages()) as LocationRow[];
                  }
                  const toAdd = extractGroupedLocationLeafOptions(rows).filter(
                    (option) => !hiddenOptionIds?.has(String(option.id))
                  );
                  // Merge with existing selection like other filters do,
                  // so pre-search selections are preserved.
                  const merged = new Map<string, { id: string; name: string }>(
                    selectedOptions.map((option) => [
                      String(option.id),
                      {
                        id: String(option.id),
                        name: option.name ?? String(option.id),
                      },
                    ])
                  );
                  toAdd.forEach((option) => {
                    if (!merged.has(option.id)) merged.set(option.id, option);
                  });
                  setSelectedOptions(Array.from(merged.values()) as ListItem[]);
                }
              : undefined
          }
        />
      </div>
    );
  }

  if (isPaginated) {
    return (
      <PaginatedOptionsList
        enabled
        onBottomReached={pg.loadMore}
        height={paginatedListHeight}
        stopAll={stopAll}
        className='subfilter-paginated-scroll'
      >
        <SubFilterOptionsList
          filterBy={filterBy}
          options={listToRender}
          selectedOptions={selectedOptions}
          onCheckboxChange={handleCheckboxChange}
          onApplyPreset={handleApplyPresetFilters}
          onDeletePreset={onDeletePreset}
          setSelectedOptions={setSelectedOptions}
        />

        {pg.loading && (
          <Menu.Item key='loading' disabled className='subfilter-loading-item'>
            <div className='subfilter-loading-inner'>
              <Spin size='small' />
            </div>
          </Menu.Item>
        )}
      </PaginatedOptionsList>
    );
  }

  // Team groups pick teams from the whole team list, not just the ones a
  // search leaves visible; hidden (tab base) teams stay out, as in the list.
  const teamSource =
    Array.isArray(secondaryFiltersSource) && secondaryFiltersSource.length
      ? secondaryFiltersSource.filter(
          (option) => !hiddenOptionIds?.has(String(option.id))
        )
      : listToRender;

  return (
    <SubFilterOptionsList
      filterBy={filterBy}
      options={listToRender}
      selectedOptions={selectedOptions}
      onCheckboxChange={handleCheckboxChange}
      onApplyPreset={handleApplyPresetFilters}
      onDeletePreset={onDeletePreset}
      setSelectedOptions={setSelectedOptions}
      allOptions={teamSource}
      searchQuery={searchQuery}
    />
  );
};

export default SubFilterBody;
